import React from 'react'
import { PanelRule, PanelSegmentSelect } from 'components/menu/panel'
import { getDefaultFilterTypeForColType, isValidFilterTypeForColType } from 'components/spreadsheet/helpers/filtering'
import { IFilterType } from 'components/spreadsheet/types'
import { FilterTypesToDescription } from 'components/spreadsheet/constants/const'
import IconSelector from 'components/spreadsheet/components/cell/components/icon'
import FilterValue from 'components/spreadsheet/components/menu/views/filter/FilterValue'
import { ILockItemProps } from '.'

// A lock rule, as a pill like a filter's: the columns it watches, the condition and value a row has
// to meet (none for "all values"), and what a match locks: the whole row, or just the cell.

const SCOPES = [
  { value: 'row', label: 'whole row' },
  { value: 'cell', label: 'this cell' }
]

interface LockItemProps extends ILockItemProps {
  onDelete: (index: number) => void
  onChange: (newLock: ILockItemProps, prevLock: ILockItemProps) => void
  index: number
  optionsOpen?: boolean
}

const LockItem: React.FC<LockItemProps> = ({
  columnId,
  columnName,
  filterType,
  value,
  multipleValues,
  applyToEntireRow,
  selectedColumns,
  columnOptions,
  kind,
  column,
  index,
  focus,
  onDelete,
  onChange,
  optionsOpen
}) => {
  const computeTypesOptions = () => {
    if (selectedColumns.length === 0) return []

    const allFilterTypes = Object.values(IFilterType)

    const commonTypes = selectedColumns.reduce<Set<IFilterType>>((acc, { value }) => {
      const col = columnOptions.find((c) => c.value === value)
      if (!col) {
        return acc
      }

      const validForCol = new Set(allFilterTypes.filter((t) => isValidFilterTypeForColType(col.kind, t)))

      if (acc.size === 0) return validForCol

      return new Set([...acc].filter((t) => validForCol.has(t)))
    }, new Set())

    return [...commonTypes].map((type) => ({
      value: type,
      label: FilterTypesToDescription[type]
    }))
  }
  const typesOptions = computeTypesOptions()

  const handleColumnChange = (selectedColumnId: string) => {
    const foundSelectedColumn = selectedColumns.find((c) => c.value === selectedColumnId)
    const newSelectedColumns = foundSelectedColumn
      ? selectedColumns.filter((c) => c.value !== selectedColumnId)
      : [
          ...selectedColumns,
          {
            value: selectedColumnId,
            label: columnOptions.find((c) => c.value === selectedColumnId)?.label ?? ''
          }
        ]

    newSelectedColumns.sort((a, b) => a.label.localeCompare(b.label))

    const allFilterTypes = Object.values(IFilterType)
    const commonTypes = newSelectedColumns.reduce<Set<IFilterType>>((acc, { value }) => {
      const col = columnOptions.find((o) => o.value === value)
      if (!col) {
        return acc
      }
      const validForCol = new Set(allFilterTypes.filter((t) => isValidFilterTypeForColType(col.kind, t)))
      return acc.size === 0 ? validForCol : new Set([...acc].filter((t) => validForCol.has(t)))
    }, new Set())

    let nextFilterType = filterType
    let nextValue = value
    let nextMultiple = multipleValues

    if (!commonTypes.has(filterType)) {
      const clickedCol = columnOptions.find((o) => o.value === selectedColumnId)
      if (!clickedCol) return
      const clickedKind = clickedCol.kind
      nextFilterType = getDefaultFilterTypeForColType(clickedKind)

      nextValue = ''
      nextMultiple = []
    }

    onChange(
      {
        columnId,
        columnName,
        filterType: nextFilterType,
        value: nextValue,
        multipleValues: nextMultiple,
        applyToEntireRow,
        selectedColumns: newSelectedColumns,
        kind,
        column,
        columnOptions,
        focus
      },
      {
        columnId,
        columnName,
        filterType,
        value,
        multipleValues,
        applyToEntireRow,
        selectedColumns,
        kind,
        column,
        columnOptions,
        focus
      }
    )
  }

  const handleFilterTypeChange = (newFilterType: IFilterType) => {
    if (newFilterType === IFilterType.one_of || newFilterType === IFilterType.not_one_of) {
      onChange({ ...prevLock, filterType: newFilterType, multipleValues: multipleValues || [] }, prevLock)
    } else if (newFilterType === IFilterType.all_values) {
      onChange({ ...prevLock, filterType: newFilterType, value: '', multipleValues: [] }, prevLock)
    } else if (value) {
      // truthy guard rather than `value !== ''` so an undefined value
      // (e.g. an all_values rule round-tripped from the server) is treated
      // as "no value to preserve" and falls through to the empty cases.
      onChange({ ...prevLock, filterType: newFilterType, value: value, multipleValues: [] }, prevLock)
    } else if (
      newFilterType === IFilterType.is_null ||
      newFilterType === IFilterType.is_not_null ||
      newFilterType === IFilterType.is_valid ||
      newFilterType === IFilterType.is_not_valid
    ) {
      onChange({ ...prevLock, filterType: newFilterType, value: '' }, prevLock)
    } else {
      onChange({ ...prevLock, filterType: newFilterType }, prevLock)
    }
  }

  const prevLock: ILockItemProps = {
    columnId,
    columnName,
    filterType,
    value,
    multipleValues,
    applyToEntireRow,
    selectedColumns,
    kind,
    column,
    columnOptions,
    focus
  }
  const selectedColumnLocksFiltered = selectedColumns.filter(
    (selectedColumn) => !!columnOptions.find((columnOption) => columnOption.value === selectedColumn.value)
  )

  if (typesOptions.length === 0 || selectedColumnLocksFiltered.length === 0) return null

  // ALL_VALUES is unconditional, so there's no value to give.
  const showValueInput = filterType !== IFilterType.all_values

  return (
    <PanelRule
      id={`lock-${index}`}
      removeLabel={`Remove lock rule on ${selectedColumnLocksFiltered.map((c) => c.label).join(', ')}`}
      onRemove={() => onDelete(index)}
      data-testid="lock-item"
    >
      <PanelSegmentSelect
        ariaLabel="Columns"
        tone="field"
        multiselect
        // Formula columns are read-only already: shown, but not to be picked (with the reason as a tooltip).
        options={columnOptions.map((option) => ({ ...option, icon: <IconSelector kind={option.kind} /> }))}
        selected={selectedColumnLocksFiltered.map((c) => c.value)}
        onPick={handleColumnChange}
        menuWidth={240}
      />
      <PanelSegmentSelect
        ariaLabel="Condition"
        tone="muted"
        options={typesOptions}
        selected={filterType ? [filterType] : []}
        onPick={(option) => handleFilterTypeChange(option as IFilterType)}
        searchable={false}
        menuWidth={160}
      />
      {showValueInput && (
        <FilterValue
          column={column}
          filterType={filterType}
          value={value ?? ''}
          multipleValues={multipleValues || []}
          onChange={(newValue: string) => onChange({ ...prevLock, value: newValue }, prevLock)}
          onChangeMultipleValues={(values: string[]) =>
            onChange({ ...prevLock, multipleValues: values || [] }, prevLock)
          }
          optionsOpen={optionsOpen}
        />
      )}
      <PanelSegmentSelect
        ariaLabel="What a match locks"
        lead="locks"
        options={SCOPES}
        selected={[applyToEntireRow ? 'row' : 'cell']}
        onPick={(scope) => onChange({ ...prevLock, applyToEntireRow: scope === 'row' }, prevLock)}
        searchable={false}
        menuWidth={140}
      />
    </PanelRule>
  )
}

export default LockItem
